import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { GetProjectManufacturerProductListingInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import {
    AddNuggetToProjectMutation,
    AddProductFamilyToProjectMutation,
    AddUniformatClassificationToProjectMutation,
    RemoveNuggetFromProjectMutation,
    RemoveProductFamilyFromProjectMutation,
    RemoveUniformatClassificationFromProjectMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import { GetProjectManufacturerProductListingQuery } from 'api/graphQL/queries/projectElements.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setIsRemoveNuggetGqlOngoing } from 'api/graphQL/reactiveVars/workspaceContent'
import { GetProjectManufacturerProductListingResponse } from 'api/graphQL/types/projectElement.types'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { usePermissionChecks } from 'components/WorkspaceEditor/hooks/usePermissionChecks'
import { useWorkspace2Actions } from 'components/WorkspaceEditor/Workspace2Store'
import {
    CreateElementInput,
    RemoveProjectElementInput,
    RemoveProjectElementResult,
} from 'models/getProjectElementNodesInput'
import { ProjectElementWithProjectNode } from 'models/projectElementNode'
import React from 'react'
import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_PRODUCT_LISTING_ACTION,
    WorkspaceAddSelectedProductCount,
    WorkspaceManufacturerSelected,
    updatedDivisionElements,
} from 'shared/utils/workspaceUtils'
import AddProjectWarningDialog from './AddProjectWarningDialog'
import { NewWorkspaceAddRemoveButton } from './Workspace2AddRemoveProjectButton'
import useBetaWorkspaceToggle from './WorkspaceRightPane/hooks/useBetaWorkspaceToggle'

const useAddToProjectButtonStyles = makeStyles((theme: Theme) => ({
    addRemoveButton: {
        minWidth: 125,
        float: 'right',
        '& button': {
            fontSize: pxToRem(14),
            '&:hover': {
                border: (isInProject: boolean) =>
                    isInProject ? `1px solid #E62325` : `1px solid #091B32`,
                backgroundColor: (isInProject: boolean) =>
                    isInProject ? '#E62325' : '#091B32',
            },
            '&:disabled': {
                borderColor: 'rgba(0, 0, 0, 0.12)',
            },
        },
        '@media (max-width: 1600px)': {
            marginTop: theme.spacing(1),
        },
        '@media (max-width: 1280px)': {
            marginTop: 0,
        },
    },
    addToProjectButton: {
        border: '1px solid #C9C9C9',
        borderRadius: pxToRem(100),
        color: '#314959',
        lineHeight: 1.43,
        height: pxToRem(26),
        width: pxToRem(100),
        fontSize: pxToRem(12),
        fontWeight: 500,
        padding: theme.spacing(0.625, 1.25),
        whiteSpace: 'nowrap',
        '&:disabled': {
            color: '#777777',
            border: '1px solid #C9C9C9',
            backgroundColor: '#D0D0D0',
        },
    },
    removeFromProjectButton: {
        border: '1px solid #D43636',
        borderRadius: pxToRem(100),
        color: '#D43636',
        lineHeight: 1.43,
        height: pxToRem(26),
        width: pxToRem(137),
        fontSize: pxToRem(12),
        fontWeight: 500,
        padding: theme.spacing(0.625, 1.25),
        whiteSpace: 'nowrap',
        '&:disabled': {
            color: '#777777',
            border: '1px solid #C9C9C9',
            backgroundColor: '#D0D0D0',
        },
    },
}))

export interface AddRemoveProjectButtonProps {
    projectElementNode: ProjectElementNode | undefined
    projectId: string
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    isOnlyAddedToProject?: boolean
    setIsAddRemoveClicked?: React.Dispatch<React.SetStateAction<boolean>>
    contentTeamFromStudioPage?: boolean
    selectedElementFormat?: string
    allElementsList?: ProjectElementNode[]
}
const AddRemoveProjectButton: React.FC<AddRemoveProjectButtonProps> = ({
    projectElementNode,
    projectId,
    updateElementList,
    isOnlyAddedToProject,
    setIsAddRemoveClicked,
    selectedElementFormat,
    allElementsList,
}) => {
    const classes = useAddToProjectButtonStyles(
        projectElementNode ? projectElementNode?.isInProject : false
    )

    const serviceWorker = navigator.serviceWorker
    const { isReviewer } = useReactiveVar(userInfo)
    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const { setIsTreeLoading } = useWorkspace2Actions()
    const setElementItemValue = useElementItemStore(
        (s) => s.setElementItemValue
    )
    const { showSharedPropertyLabel } = usePermissionChecks([], null)
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const [showAddProjectDialog, setShowAddProjectDialog] = React.useState(
        false
    )
    const [
        showRemoveProjectDialog,
        setShowRemoveProjectDialog,
    ] = React.useState(false)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        addAssemblyToProject,
        {
            loading: addAssemblyLoading,
            error: addAssemblyError,
            data: addAssemblyData,
        },
    ] = useMutation<
        {
            addUniformatClassificationToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddUniformatClassificationToProjectMutation)

    const [
        addFamilyToProject,
        {
            loading: addProductFamilyLoading,
            error: addProductFamilyError,
            data: addProductFamilyData,
        },
    ] = useMutation<
        {
            addProductFamilyToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddProductFamilyToProjectMutation)
    const [
        addProductTypeToProject,
        {
            loading: addProductTypeLoading,
            error: addProductTypeError,
            data: addProductTypeData,
        },
    ] = useMutation<
        {
            addNuggetToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddNuggetToProjectMutation)

    const [
        removeAssemblyFromProject,
        {
            loading: removeAssemblyLoading,
            error: removeAssemblyError,
            data: removeAssemblyData,
        },
    ] = useMutation<
        {
            removeUniformatClassificationFromProject: RemoveProjectElementResult | null
        },
        {
            input: RemoveProjectElementInput
        }
    >(RemoveUniformatClassificationFromProjectMutation, { onError: () => {} })

    const [
        removeFamilyFromProject,
        {
            loading: removeFamilyLoading,
            error: removeFamilyError,
            data: removeFamilyData,
        },
    ] = useMutation<
        {
            removeProductFamilyFromProject: RemoveProjectElementResult | null
        },
        {
            input: RemoveProjectElementInput
        }
    >(RemoveProductFamilyFromProjectMutation, { onError: () => {} })
    const [getSelectedProducts] = useLazyQuery<
        GetProjectManufacturerProductListingResponse
    >(GetProjectManufacturerProductListingQuery, {
        displayName: 'ProjectProductListing',
        fetchPolicy: 'no-cache',
        variables: {
            input: {
                projectId,
                projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
            } as GetProjectManufacturerProductListingInput,
        },
        onCompleted: (data) => {
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_PRODUCT_LISTING_ACTION]: {
                    selectedProducts:
                        data?.getProjectManufacturerProductListing?.length ?? 0,
                } as WorkspaceAddSelectedProductCount,
            })
            setIsRemoveNuggetGqlOngoing(false)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })
    const [
        removeProductTypeFromProject,
        {
            loading: removeProductTypeLoading,
            error: removeProductTypeError,
            data: removeProductTypeData,
        },
    ] = useMutation<
        {
            removeNuggetFromProject: RemoveProjectElementResult | null
        },
        {
            input: RemoveProjectElementInput
        }
    >(RemoveNuggetFromProjectMutation, {
        onCompleted: () => {
            // send serviceWorker change
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                    listingId: '',
                    isSelected: false,
                    isBOD: false,
                    removeSelected: true,
                } as WorkspaceManufacturerSelected,
            })
            getSelectedProducts()
        },
        onError: () => {
            setIsRemoveNuggetGqlOngoing(false)
        },
    })

    const confirmationDialogContent = React.useMemo(
        () =>
            projectElementNode?.elementType === 'PRODUCTTYPE' &&
            projectElementNode?.isProjectOnly ? (
                <>
                    This action will permanently remove any copied product types
                    from your project.
                </>
            ) : (
                <>
                    This action will permanently remove associations and
                    properties from your project.
                </>
            ),
        [projectElementNode]
    )

    const clearElementItemValue = React.useCallback(() => {
        setElementItemValue({
            ...elementItemValue,
            elementId: '',
            elementType: '',
            baseElementId: '',
        })
        setIsTreeLoading(true)
    }, [elementItemValue, setElementItemValue, setIsTreeLoading])

    const handleError = React.useCallback(
        (error: Error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'AddRemoveProjectButtonError',
                },
            })
            setIsTreeLoading(false)
        },
        [showGlobalSnackbar, setIsTreeLoading]
    )

    const onAddToProject = React.useCallback(() => {
        const mutationOption = {
            variables: {
                input: {
                    projectId,
                    baseElementId: projectElementNode!.baseElementId,
                    nodeId: projectElementNode!.id,
                    treePath: projectElementNode!.treePath,
                    isProductFamilyGroupView:
                        selectedElementFormat === 'DIVISIONS',
                },
            },
        }

        setIsAddRemoveClicked?.(true)

        if (projectElementNode!.elementType === 'ASSEMBLY') {
            addAssemblyToProject(mutationOption)
        }

        if (projectElementNode!.elementType === 'PRODUCTFAMILY') {
            addFamilyToProject(mutationOption)
                .then(() => {
                    clearElementItemValue()
                })
                .catch((error) => {
                    handleError(error)
                })
        }

        if (projectElementNode!.elementType === 'PRODUCTTYPE') {
            addProductTypeToProject(mutationOption)
                .then(() => {
                    clearElementItemValue()
                })
                .catch((error) => {
                    handleError(error)
                })
        }
    }, [
        projectId,
        projectElementNode,
        elementItemValue,
        setIsAddRemoveClicked,
        addAssemblyToProject,
        addFamilyToProject,
        addProductTypeToProject,
    ])

    const handleContinueAddToProject = React.useCallback(() => {
        onAddToProject()
        setShowAddProjectDialog(false)
    }, [onAddToProject, setShowAddProjectDialog])

    const handleClickAddToProject = React.useCallback(
        (_: React.MouseEvent<Element, MouseEvent>) => {
            if (
                projectElementNode?.elementType === 'PRODUCTTYPE' &&
                projectElementNode?.isBasedFromManufacturerOption
            ) {
                setShowAddProjectDialog(true)
            } else {
                onAddToProject()
                setShowAddProjectDialog(false)
            }
        },
        [projectElementNode]
    )

    const handleClickRemoveFromProject = React.useCallback(
        (_: React.MouseEvent<Element, MouseEvent>) => {
            setShowRemoveProjectDialog(true)
        },
        []
    )

    const handleAddToProjectDialogClose = React.useCallback(() => {
        setShowAddProjectDialog(false)
    }, [])

    const handleRemoveProjectDialogClose = React.useCallback(() => {
        setShowRemoveProjectDialog(false)
    }, [])

    const onRemoveFromProject = React.useCallback(() => {
        handleRemoveProjectDialogClose()
        const mutationOption = {
            variables: {
                input: {
                    projectId: projectId ?? '',
                    nodeId: projectElementNode!.id,
                    onlyAddedToProject: isOnlyAddedToProject ?? false,
                    isProductFamilyGroupView:
                        selectedElementFormat === 'DIVISIONS',
                },
            },
        }

        setIsAddRemoveClicked && setIsAddRemoveClicked(true)

        if (projectElementNode!.elementType === 'ASSEMBLY') {
            removeAssemblyFromProject(mutationOption)
        }

        if (projectElementNode!.elementType === 'PRODUCTFAMILY') {
            removeFamilyFromProject(mutationOption)
                .then(() => {
                    clearElementItemValue()
                })
                .catch((error) => {
                    handleError(error)
                })
        }

        if (projectElementNode!.elementType === 'PRODUCTTYPE') {
            setIsRemoveNuggetGqlOngoing(true)
            removeProductTypeFromProject(mutationOption)
                .then(() => {
                    clearElementItemValue()
                })
                .catch((error) => {
                    handleError(error)
                })
        }
    }, [
        projectId,
        projectElementNode,
        isOnlyAddedToProject,
        elementItemValue,
        setIsAddRemoveClicked,
        handleRemoveProjectDialogClose,
        removeAssemblyFromProject,
        removeFamilyFromProject,
        removeProductTypeFromProject,
    ])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (addAssemblyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addAssemblyError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addAssemblyToProjectError',
                },
            })
        }

        if (addProductFamilyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addProductFamilyError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addProductFamilyToProjectError',
                },
            })
        }

        if (addProductTypeError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addProductTypeError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addProductTypeToProjectError',
                },
            })
        }

        if (removeAssemblyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        removeAssemblyError,
                        'Error while removing this element from project.'
                    ),
                    variant: 'error',
                    id: 'removeAssemblyError',
                },
            })
        }

        if (removeFamilyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        removeFamilyError,
                        'Error while removing this element from project.'
                    ),
                    variant: 'error',
                    id: 'removeFamilyError',
                },
            })
        }

        if (removeProductTypeError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        removeProductTypeError,
                        'Error while removing this element from project.'
                    ),
                    variant: 'error',
                    id: 'removeProductTypeError',
                },
            })
        }
    }, [
        addAssemblyError,
        addProductFamilyError,
        addProductTypeError,
        removeAssemblyError,
        removeFamilyError,
        removeProductTypeError,
        showGlobalSnackbar,
    ])

    React.useEffect(() => {
        if (
            !!removeAssemblyData &&
            !!removeAssemblyData.removeUniformatClassificationFromProject
        ) {
            if (
                removeAssemblyData.removeUniformatClassificationFromProject
                    .removedElementNodes.length > 0
            ) {
                if (selectedElementFormat === 'DIVISIONS') {
                    updateElementList!(
                        updatedDivisionElements(
                            removeAssemblyData
                                .removeUniformatClassificationFromProject
                                .removedElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeAssemblyData
                                .removeUniformatClassificationFromProject
                                .parentElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeAssemblyData
                                .removeUniformatClassificationFromProject
                                .deletedElementNodes!,
                            allElementsList
                        )
                    )
                } else {
                    updateElementList!(
                        removeAssemblyData
                            .removeUniformatClassificationFromProject
                            .removedElementNodes,
                        removeAssemblyData
                            .removeUniformatClassificationFromProject
                            .parentElementNodes,
                        removeAssemblyData
                            .removeUniformatClassificationFromProject
                            .deletedElementNodes
                    )
                }

                removeAssemblyData.removeUniformatClassificationFromProject = null
            }
        }
    }, [
        removeAssemblyData,
        updateElementList,
        selectedElementFormat,
        updatedDivisionElements,
        allElementsList,
    ])

    React.useEffect(() => {
        if (
            !!removeFamilyData &&
            !!removeFamilyData.removeProductFamilyFromProject
        ) {
            if (
                removeFamilyData.removeProductFamilyFromProject
                    .removedElementNodes.length > 0 ||
                removeFamilyData.removeProductFamilyFromProject
                    .deletedElementNodes!.length > 0
            ) {
                if (selectedElementFormat === 'DIVISIONS') {
                    updateElementList!(
                        updatedDivisionElements(
                            removeFamilyData.removeProductFamilyFromProject
                                .removedElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeFamilyData.removeProductFamilyFromProject
                                .parentElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeFamilyData.removeProductFamilyFromProject
                                .deletedElementNodes!,
                            allElementsList
                        )
                    )
                } else {
                    updateElementList!(
                        removeFamilyData.removeProductFamilyFromProject
                            ?.removedElementNodes,
                        removeFamilyData.removeProductFamilyFromProject
                            ?.parentElementNodes,
                        removeFamilyData.removeProductFamilyFromProject
                            ?.deletedElementNodes
                    )
                }

                removeFamilyData.removeProductFamilyFromProject = null
            }
        }
    }, [
        removeFamilyData,
        updateElementList,
        selectedElementFormat,
        updatedDivisionElements,
        allElementsList,
    ])

    React.useEffect(() => {
        if (
            !!removeProductTypeData &&
            !!removeProductTypeData.removeNuggetFromProject
        ) {
            if (
                removeProductTypeData.removeNuggetFromProject
                    .removedElementNodes.length > 0 ||
                (!!removeProductTypeData.removeNuggetFromProject
                    .deletedElementNodes &&
                    removeProductTypeData.removeNuggetFromProject
                        .deletedElementNodes.length > 0)
            ) {
                if (selectedElementFormat === 'DIVISIONS') {
                    updateElementList!(
                        updatedDivisionElements(
                            removeProductTypeData.removeNuggetFromProject
                                .removedElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeProductTypeData.removeNuggetFromProject
                                .parentElementNodes,
                            allElementsList
                        ),
                        updatedDivisionElements(
                            removeProductTypeData.removeNuggetFromProject
                                .deletedElementNodes!,
                            allElementsList
                        )
                    )
                } else {
                    updateElementList!(
                        removeProductTypeData.removeNuggetFromProject
                            ?.removedElementNodes,
                        removeProductTypeData.removeNuggetFromProject
                            ?.parentElementNodes,
                        removeProductTypeData.removeNuggetFromProject
                            ?.deletedElementNodes
                    )
                }
                removeProductTypeData.removeNuggetFromProject = null
            }
        }
    }, [
        removeProductTypeData,
        updateElementList,
        selectedElementFormat,
        updatedDivisionElements,
        allElementsList,
    ])

    React.useEffect(() => {
        if (
            !!addAssemblyData &&
            !!addAssemblyData.addUniformatClassificationToProject
        ) {
            if (
                (
                    addAssemblyData.addUniformatClassificationToProject
                        .projectElementNodes || []
                ).length > 0
            ) {
                updateElementList!(
                    selectedElementFormat === 'DIVISIONS'
                        ? updatedDivisionElements(
                              addAssemblyData
                                  .addUniformatClassificationToProject
                                  ?.projectElementNodes,
                              allElementsList
                          )
                        : addAssemblyData.addUniformatClassificationToProject
                              ?.projectElementNodes
                )
                addAssemblyData.addUniformatClassificationToProject = null
            }
        }
    }, [
        addAssemblyData,
        updateElementList,
        allElementsList,
        selectedElementFormat,
        updatedDivisionElements,
    ])

    React.useEffect(() => {
        if (
            !!addProductFamilyData &&
            !!addProductFamilyData.addProductFamilyToProject
        ) {
            if (
                (
                    addProductFamilyData.addProductFamilyToProject
                        .projectElementNodes || []
                ).length > 0
            ) {
                updateElementList!(
                    selectedElementFormat === 'DIVISIONS'
                        ? updatedDivisionElements(
                              addProductFamilyData?.addProductFamilyToProject
                                  ?.projectElementNodes,
                              allElementsList
                          )
                        : addProductFamilyData?.addProductFamilyToProject
                              ?.projectElementNodes
                )
                addProductFamilyData.addProductFamilyToProject = null
            }
        }
    }, [
        addProductFamilyData,
        updateElementList,
        selectedElementFormat,
        updatedDivisionElements,
        allElementsList,
    ])

    React.useEffect(() => {
        if (!!addProductTypeData && !!addProductTypeData.addNuggetToProject) {
            if (
                (
                    addProductTypeData.addNuggetToProject.projectElementNodes ||
                    []
                ).length > 0
            ) {
                updateElementList!(
                    selectedElementFormat === 'DIVISIONS'
                        ? updatedDivisionElements(
                              addProductTypeData?.addNuggetToProject
                                  ?.projectElementNodes,
                              allElementsList
                          )
                        : addProductTypeData.addNuggetToProject
                              ?.projectElementNodes
                )
                addProductTypeData.addNuggetToProject = null
            }
        }
    }, [
        addProductTypeData,
        updateElementList,
        selectedElementFormat,
        allElementsList,
        updatedDivisionElements,
    ])

    if (!projectElementNode) return null
    return (
        <div
            className={
                isBetaWorkspaceToggleEnabled ? '' : classes.addRemoveButton
            }
        >
            {!projectElementNode.isInProject &&
                (isBetaWorkspaceToggleEnabled ? (
                    <NewWorkspaceAddRemoveButton
                        label="Add to project"
                        isDisabled={
                            addAssemblyLoading ||
                            addProductFamilyLoading ||
                            addProductTypeLoading ||
                            isReviewer ||
                            (selectedElementFormat === 'DIVISIONS' &&
                                projectElementNode?.elementType ===
                                    'ASSEMBLY') ||
                            (isBetaWorkspaceToggleEnabled &&
                                showSharedPropertyLabel)
                        }
                        isLoading={
                            addAssemblyLoading ||
                            addProductFamilyLoading ||
                            addProductTypeLoading
                        }
                        className={classes.addToProjectButton}
                        dataTestId="addToProject"
                        onClick={handleClickAddToProject}
                    />
                ) : (
                    <RoundButton
                        size="medium"
                        hasShadow={false}
                        disabled={
                            addAssemblyLoading ||
                            addProductFamilyLoading ||
                            addProductTypeLoading ||
                            isReviewer ||
                            (selectedElementFormat === 'DIVISIONS' &&
                                projectElementNode?.elementType ===
                                    'ASSEMBLY') ||
                            (isBetaWorkspaceToggleEnabled &&
                                showSharedPropertyLabel)
                        }
                        loading={
                            addAssemblyLoading ||
                            addProductFamilyLoading ||
                            addProductTypeLoading
                        }
                        color="#fff"
                        borderColor="#304959"
                        backgroundColor="#304959"
                        data-testid="addToProject"
                        hoverColor="#091B32"
                        onClick={handleClickAddToProject}
                    >
                        Add To Project
                    </RoundButton>
                ))}

            {projectElementNode.isInProject &&
                (isBetaWorkspaceToggleEnabled ? (
                    <NewWorkspaceAddRemoveButton
                        label="Remove from project"
                        isDisabled={
                            removeAssemblyLoading ||
                            removeFamilyLoading ||
                            removeProductTypeLoading ||
                            isReviewer ||
                            (selectedElementFormat === 'DIVISIONS' &&
                                projectElementNode?.elementType ===
                                    'ASSEMBLY') ||
                            (isBetaWorkspaceToggleEnabled &&
                                showSharedPropertyLabel)
                        }
                        isLoading={
                            removeAssemblyLoading ||
                            removeFamilyLoading ||
                            removeProductTypeLoading
                        }
                        className={classes.removeFromProjectButton}
                        dataTestId="removeFromProject"
                        onClick={handleClickRemoveFromProject}
                    />
                ) : (
                    <RoundButton
                        size="medium"
                        hasShadow={false}
                        disabled={
                            removeAssemblyLoading ||
                            removeFamilyLoading ||
                            removeProductTypeLoading ||
                            isReviewer ||
                            (selectedElementFormat === 'DIVISIONS' &&
                                projectElementNode?.elementType ===
                                    'ASSEMBLY') ||
                            (isBetaWorkspaceToggleEnabled &&
                                showSharedPropertyLabel)
                        }
                        loading={
                            removeAssemblyLoading ||
                            removeFamilyLoading ||
                            removeProductTypeLoading
                        }
                        color="#fff"
                        borderColor="#EB5757"
                        backgroundColor="#EB5757"
                        data-testid="removeFromProject"
                        hoverColor="#E62325"
                        onClick={handleClickRemoveFromProject}
                    >
                        Remove From Project
                    </RoundButton>
                ))}
            <ConfirmationDialog
                openDialog={showRemoveProjectDialog}
                handleDialogClose={handleRemoveProjectDialogClose}
                dialogActionCallback={onRemoveFromProject}
                contentNode={confirmationDialogContent}
                size="small"
            />
            <AddProjectWarningDialog
                open={showAddProjectDialog}
                onCancel={handleAddToProjectDialogClose}
                onSubmit={handleContinueAddToProject}
            />
        </div>
    )
}

export default AddRemoveProjectButton
